import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory, Redirect } from 'react-router-dom';
import Spinner from 'react-spinkit';
import { useSelector } from 'react-redux';
import { RootState } from './ApplicationState';
import {
  acceptClassroomInvite,
  declineClassroomInvite,
  getMyCoTeacherMemberships,
  leaveClassroom,
  MyCoTeacherMembershipItem,
  isCoTeacherGeneralUIEnabled,
} from './BackendInterface';
import { ClassroomMembershipStatus } from '@peekapak/types/DataProtocolTypes';
import { Dialog, DialogContent } from './components/Dialog';
import PrimaryButton from './components/PrimaryButton/PrimaryButton';
import SecondaryButton from './components/SecondaryButton/SecondaryButton';
import styles from './MyCoTeacherMembershipsPage.module.scss';

type InviteAction = 'accept' | 'decline';

type PendingAction = {
  action: InviteAction;
  classroomId: string;
  sig?: string;
};

function statusBadge(status: ClassroomMembershipStatus): JSX.Element {
  if (status === ClassroomMembershipStatus.PENDING) {
    return (
      <span className='rounded bg-amber-100 px-[0.6rem] py-[0.2rem] text-[1.2rem] font-semibold text-amber-800'>
        Pending
      </span>
    );
  }
  if (status === ClassroomMembershipStatus.DECLINED) {
    return (
      <span className='rounded bg-red-50 px-[0.6rem] py-[0.2rem] text-[1.2rem] font-semibold text-red-600'>
        Declined
      </span>
    );
  }
  return (
    <span className='rounded bg-green-50 px-[0.6rem] py-[0.2rem] text-[1.2rem] font-semibold text-green-700'>
      Active
    </span>
  );
}

function getInviteActionMessage(error: unknown): string {
  const message = (error as Error)?.message?.toLowerCase?.() || '';

  if (message.includes('already accepted')) {
    return 'This invitation has already been accepted.';
  }

  if (message.includes('already declined')) {
    return 'This invitation has already been declined.';
  }

  if (message.includes('not found') || message.includes('no pending')) {
    return 'This invitation is no longer available.';
  }

  if (message.includes('invalid invitation signature')) {
    return 'This invitation link is invalid.';
  }

  return (error as Error)?.message || 'This invitation was already handled.';
}

interface MyCoTeacherMembershipsPageProps {
  showTitle?: boolean;
}

const MyCoTeacherMembershipsPage = ({ showTitle = true }: MyCoTeacherMembershipsPageProps): JSX.Element => {
  const [items, setItems] = useState<MyCoTeacherMembershipItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [dialogMessage, setDialogMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const location = useLocation();
  const history = useHistory();
  const userProfile = useSelector((state: RootState) => state.user.userProfile);
  const [featureEnabled, setFeatureEnabled] = useState<boolean | null>(null);

  const actionFromQuery = useMemo(() => {
    const query = new URLSearchParams(location.search);
    const action = query.get('action');
    const classroomId = query.get('classroomId');
    const sig = query.get('sig');

    if (!action || !classroomId) {
      return null;
    }

    if (action !== 'accept' && action !== 'decline') {
      return null;
    }

    return {
      action,
      classroomId,
      sig: sig || undefined,
    } as PendingAction;
  }, [location.search]);

  const visibleItems = useMemo(
    () =>
      items.filter((item) => !(item.status === ClassroomMembershipStatus.ACTIVE && item.otherCoTeachers.length === 0)),
    [items],
  );

  const clearInviteQuery = useCallback(() => {
    if (location.pathname !== '/co-teacher-memberships' || location.search) {
      history.replace('/co-teacher-memberships');
    }
  }, [history, location.pathname, location.search]);

  useEffect(() => {
    const districtId = userProfile?.district || '';
    const schoolId = userProfile?.schoolId || '';

    isCoTeacherGeneralUIEnabled(districtId, schoolId).then((enabled) => {
      setFeatureEnabled(enabled);
    });
  }, [userProfile?.district, userProfile?.schoolId]);

  useEffect(() => {
    void loadMemberships();
  }, []);

  useEffect(() => {
    if (!actionFromQuery || isLoading) {
      return;
    }

    const membership = items.find((item) => item.classroomId === actionFromQuery.classroomId);

    if (!membership || membership.status === ClassroomMembershipStatus.REMOVED) {
      setDialogMessage('This invitation no longer exists.');
      clearInviteQuery();
      return;
    }

    if (membership.status === ClassroomMembershipStatus.ACTIVE) {
      setDialogMessage('This invitation has already been accepted.');
      clearInviteQuery();
      return;
    }

    if (membership.status === ClassroomMembershipStatus.DECLINED) {
      setDialogMessage('This invitation has already been declined.');
      clearInviteQuery();
      return;
    }

    if (membership.status !== ClassroomMembershipStatus.PENDING) {
      setDialogMessage('This invitation no longer exists.');
      clearInviteQuery();
      return;
    }

    setPendingAction(actionFromQuery);
  }, [actionFromQuery, clearInviteQuery, isLoading, items]);

  const selectedClassroom = useMemo(() => {
    if (!pendingAction) {
      return null;
    }

    return items.find((item) => item.classroomId === pendingAction.classroomId) || null;
  }, [items, pendingAction]);

  function closePendingActionDialog() {
    setPendingAction(null);
    if (actionFromQuery) {
      clearInviteQuery();
    }
  }

  async function loadMemberships() {
    try {
      setIsLoading(true);
      const memberships = await getMyCoTeacherMemberships();
      setItems(memberships);
      setErrorMessage(null);
    } catch (error) {
      setErrorMessage((error as Error).message || 'Unable to load your co-teacher memberships right now.');
    } finally {
      setIsLoading(false);
    }
  }

  async function runAction(payload: PendingAction) {
    setIsSaving(true);
    try {
      if (payload.action === 'accept') {
        await acceptClassroomInvite(payload.classroomId, payload.sig);
        setDialogMessage('Invitation accepted.');
      } else {
        await declineClassroomInvite(payload.classroomId, payload.sig);
        setDialogMessage('Invitation declined.');
      }
      await loadMemberships();
    } catch (error) {
      setDialogMessage(getInviteActionMessage(error));
    } finally {
      setIsSaving(false);
      setPendingAction(null);
      clearInviteQuery();
    }
  }

  async function onLeaveClassroom(classroomId: string) {
    setIsSaving(true);
    try {
      await leaveClassroom(classroomId);
      setDialogMessage('You left the classroom.');
      await loadMemberships();
    } catch (error) {
      setErrorMessage((error as Error).message || 'Unable to leave classroom right now.');
    } finally {
      setIsSaving(false);
    }
  }

  if (featureEnabled === null) {
    return null; // Still loading feature toggle
  }

  if (!featureEnabled) {
    return <Redirect to='/accountSettings' />;
  }

  return (
    <div className={`${styles.pageContainer} mx-auto max-w-[900px] px-[2.4rem] py-[2.4rem]`}>
      {/* Page title */}
      {showTitle && (
        <div className='mb-[2.4rem]'>
          <div className={styles.pageTitle}>My Co-Teacher Memberships</div>
        </div>
      )}

      {/* Error banner */}
      {errorMessage ? (
        <div className='mb-[1.6rem] rounded-[8px] border border-red-200 bg-red-50 px-[1.6rem] py-[1.2rem] text-[1.4rem] text-red-700'>
          {errorMessage}
        </div>
      ) : null}

      {/* Loading */}
      {isLoading ? (
        <div className='flex items-center justify-center gap-[1rem] py-[4rem]'>
          <Spinner name='line-scale' />
          <span className="font-['GT-Walsheim-Pro-Regular'] text-[1.4rem] text-[#86939E]">Loading memberships...</span>
        </div>
      ) : visibleItems.length === 0 ? (
        <div className='rounded-[12px] border border-[#e5e9ef] bg-white px-[2.4rem] py-[3.2rem] text-center'>
          <p className="m-0 font-['GT-Walsheim-Pro-Regular'] text-[1.6rem] text-[#86939E]">
            You do not have any co-teacher memberships right now.
          </p>
        </div>
      ) : (
        <div className='flex flex-col gap-[1.2rem]'>
          {visibleItems.map((item) => (
            <div
              key={`${item.classroomId}-${item.status}`}
              className='flex items-start justify-between gap-[2rem] rounded-[12px] border border-[#e5e9ef] bg-white px-[2rem] py-[1.6rem] shadow-sm'
            >
              {/* Left: classroom info */}
              <div className='flex min-w-0 flex-1 flex-col gap-[0.8rem]'>
                <div className='flex flex-wrap items-center gap-[1rem]'>
                  <span className="font-['GT-Walsheim-Pro-Bold'] text-[1.8rem] leading-tight text-[#1f2937]">
                    {item.classroomName}
                  </span>
                  {statusBadge(item.status)}
                </div>

                {/* Co-teachers */}
                <div className="font-['GT-Walsheim-Pro-Regular'] text-[1.3rem] text-[#86939E]">
                  <span className='font-semibold'>Co-teachers:&nbsp;</span>
                  {item.otherCoTeachers.length === 0
                    ? 'None yet'
                    : item.otherCoTeachers
                        .map((teacher) =>
                          teacher.status === ClassroomMembershipStatus.PENDING
                            ? `${teacher.teacherName} (pending)`
                            : teacher.teacherName,
                        )
                        .join(', ')}
                </div>

                {/* Students */}
                <div className="font-['GT-Walsheim-Pro-Regular'] text-[1.3rem] text-[#86939E]">
                  <span className='font-semibold'>Students:&nbsp;</span>
                  {item.studentsPreview.length > 0
                    ? `${item.studentsPreview.join(', ')}${item.studentCount > item.studentsPreview.length ? ` +${item.studentCount - item.studentsPreview.length} more` : ''}`
                    : 'No students'}
                </div>
              </div>

              {/* Right: actions */}
              <div className='flex shrink-0 items-center gap-[0.8rem] pt-[0.2rem]'>
                {item.status === ClassroomMembershipStatus.ACTIVE ? (
                  <SecondaryButton small disabled={isSaving} onClick={() => void onLeaveClassroom(item.classroomId)}>
                    Leave
                  </SecondaryButton>
                ) : item.status === ClassroomMembershipStatus.PENDING ? (
                  <>
                    <SecondaryButton
                      small
                      disabled={isSaving}
                      onClick={() => setPendingAction({ action: 'accept', classroomId: item.classroomId })}
                    >
                      Accept
                    </SecondaryButton>
                    <SecondaryButton
                      small
                      disabled={isSaving}
                      onClick={() => setPendingAction({ action: 'decline', classroomId: item.classroomId })}
                    >
                      Decline
                    </SecondaryButton>
                  </>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirmation dialog */}
      <Dialog open={pendingAction !== null} onOpenChange={(isOpen) => !isOpen && closePendingActionDialog()}>
        <DialogContent>
          <h3 className="m-0 mb-[1rem] font-['GT-Walsheim-Pro-Bold'] text-[2rem] text-[#012A73]">Confirm</h3>
          {pendingAction ? (
            <p className="font-['GT-Walsheim-Pro-Regular'] text-[1.5rem] text-[#1f2937]">
              Do you want to <strong className="font-['GT-Walsheim-Pro-Bold']">{pendingAction.action}</strong> the
              invitation for{' '}
              <strong className="font-['GT-Walsheim-Pro-Bold']">
                {selectedClassroom?.classroomName || pendingAction.classroomId}
              </strong>
              ?
            </p>
          ) : null}
          <div className='mt-[2rem] flex items-center justify-end gap-[1rem]'>
            <SecondaryButton small onClick={closePendingActionDialog} disabled={isSaving}>
              Cancel
            </SecondaryButton>
            <PrimaryButton small onClick={() => pendingAction && void runAction(pendingAction)} disabled={isSaving}>
              Confirm
            </PrimaryButton>
          </div>
        </DialogContent>
      </Dialog>

      {/* Info / outcome dialog */}
      <Dialog open={dialogMessage !== null} onOpenChange={(isOpen) => !isOpen && setDialogMessage(null)}>
        <DialogContent>
          <h3 className="m-0 mb-[1rem] font-['GT-Walsheim-Pro-Bold'] text-[2rem] text-[#012A73]">
            Co-teacher invitation
          </h3>
          <p className="font-['GT-Walsheim-Pro-Regular'] text-[1.5rem] text-[#1f2937]">{dialogMessage}</p>
          <div className='mt-[2rem] flex justify-end'>
            <PrimaryButton small onClick={() => setDialogMessage(null)}>
              Okay
            </PrimaryButton>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyCoTeacherMembershipsPage;
